import {isBefore} from 'date-fns';
import {z} from 'zod';

import {nth} from 'ramda';

import i18n from '@dms/i18n';

import {parseDate} from 'shared/web';

import {DefinitionFormType} from './types';

const cashRegisterLimitAmountSchema = z.object({
  amount: z
    .string()
    .min(1, i18n.t('general.validations.fieldIsRequired'))
    .refine(
      (value) => {
        if (!value || isNaN(Number(value))) {
          return false;
        }
        const parts = value.split('.');
        return parts.length <= 2 && parts.every((part) => /^\d+$/.test(part));
      },
      {message: i18n.t('general.validations.mustBeNumber')}
    )
    .refine(
      (value) => {
        if (!value) {
          return false;
        }
        const parts = value.split('.');
        return parts.length === 1 || (parts.length === 2 && parts[1].length <= 2);
      },
      {message: i18n.t('general.validations.maximumDecimalPlaces', {maximum: 2})}
    ),
  currency: z.string().nullable(),
});

const cashRegisterLimitRestrictionsSchema = (defaultValue?: DefinitionFormType) =>
  z
    .array(
      z.object({
        amount: cashRegisterLimitAmountSchema,
        documentTypes: z
          .array(z.enum(['income', 'expense'] as const))
          .min(1, i18n.t('general.validations.fieldIsRequired'))
          .transform((val) => (val === null ? [] : val)),
        validFrom: z.date(),
        type: z.enum(['off', 'disallow', 'notice']),
      })
    )
    .superRefine((restrictions, ctx) => {
      const restrictionsAsDateString = restrictions.map((v) =>
        parseDate(v.validFrom).toDateString()
      );
      const nowDate = parseDate(new Date().toDateString()).getTime();

      // Check for unique validFrom dates within the parent array context
      restrictions.forEach((value, index) => {
        const restrictionValidFromAsDateString = nth(index, restrictionsAsDateString) as string;
        const previousItem = restrictions[index - 1];

        // Check if there is duplicate date in validations
        const duplicateDate = restrictionsAsDateString.filter(
          (date) => date === restrictionValidFromAsDateString
        );
        if (duplicateDate.length > 1) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: i18n.t('entity.accounting.validations.validFromUnique'),
            path: [index, 'validFrom'],
          });
        }

        const validFromDate = parseDate(restrictionValidFromAsDateString).getTime();

        // Check if date already exists in values (values are passed only while editing)
        const existingItem = defaultValue?.restrictions?.find(
          (item) => item.validFrom?.toDateString() === restrictionValidFromAsDateString
        );

        if (existingItem) {
          return;
        }

        // Check if field value is from the past
        if (validFromDate < nowDate) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: i18n.t('entity.accounting.validations.noPast'),
            path: [index, 'validFrom'],
          });
        }

        // Check if dates are in chronological order
        if (
          previousItem?.validFrom &&
          isBefore(parseDate(value.validFrom), previousItem.validFrom)
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: i18n.t('entity.cashRegisterLimits.validations.noBeforeOtherDates'),
            path: [index, 'validFrom'],
          });
        }
      });
    });

export const cashRegisterLimitValidationSchema = (values?: DefinitionFormType) =>
  z
    .object({
      currency: z.string(),
      branchId: z.string().nullable(),
      restrictions: cashRegisterLimitRestrictionsSchema(values),
      system: z.boolean(),
    })
    .superRefine((value, ctx) => {
      if (!value.system && !value.branchId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: i18n.t('general.validations.fieldIsRequired'),
          path: ['branchId'],
        });
      }
    });
